import { useState, useEffect, useMemo } from 'react'
import {
	EuiComboBox,
	EuiComboBoxOptionOption,
	EuiText
} from '@equipmentshare/ds2'
import { usePurchaseOrdersQuery } from 'graphql/costcapture'
import { GRAPHQL_ENDPOINTS } from 'config/apollo-client'

type PurchaseOrderSelectProps = {
	value?: {
		purchaseOrderId: string,
		vendorId: string,
	},
	onChange: ( selection: { purchaseOrderId: string, vendorId: string } | undefined ) => void,
	isInvalid?: boolean,
}

type PurchaseOrderOption = EuiComboBoxOptionOption<string> & {
	value: string,
}

export default function PurchaseOrderSelect( {
	value,
	onChange,
	isInvalid = false
}: PurchaseOrderSelectProps ) {
	const [ searchValue, setSearchValue ] = useState( '' )
	const [ selectedOptions, setSelectedOptions ] = useState<EuiComboBoxOptionOption<string>[]>( [] )

	// Query purchase orders with search and CostCapture context
	const { data, loading, error } = usePurchaseOrdersQuery( {
		variables: { search: searchValue },
		context: {
			endpoint: GRAPHQL_ENDPOINTS.COST_CAPTURE,
			debounceKey: 'purchaseOrdersQuery-debounce'
		},
	} )

	// Convert GraphQL data to combo box options
	const options = useMemo<PurchaseOrderOption[]>( () => {
		if ( !data?.purchaseOrders?.edges ) {
			return []
		}

		return data.purchaseOrders.edges.map( ( edge ) => {
			const po = edge.node
			return {
				label: `${po.purchaseOrderNumber ?? 'Unknown'} - ${po.vendor.name} ($${po.totalRequestedCost.amount})`,
				value: po.id
			}
		} )
	}, [ data ] )

	// Update selected options when external value changes
	useEffect( () => {
		if ( value?.purchaseOrderId ) {
			const option = options.find( ( opt ) => opt.value === value.purchaseOrderId )
			if ( option ) {
				setSelectedOptions( [ option ] )
			}
		} else {
			setSelectedOptions( [] )
		}
	}, [ value, options ] )

	const onSearchChange = ( newSearchValue: string ) => {
		setSearchValue( newSearchValue )
	}

	const onSelectionChange = ( newSelectedOptions: EuiComboBoxOptionOption<string>[] ) => {
		setSelectedOptions( newSelectedOptions )

		if ( newSelectedOptions.length > 0 ) {
			const selectedValue = newSelectedOptions[ 0 ].value
			// Find the purchase order data from the GraphQL response
			const selectedPO = data?.purchaseOrders?.edges?.find(
				( edge ) => edge?.node.id === selectedValue
			)?.node

			if ( selectedPO ) {
				onChange( {
					purchaseOrderId: selectedPO.id,
					vendorId: selectedPO.vendor.id
				} )
			}
		} else {
			onChange( undefined )
		}
	}

	if ( error ) {
		return (
			<EuiText size="s" color="danger">
				Error loading purchase orders. Please try again.
			</EuiText>
		)
	}

	return (
		<EuiComboBox
			placeholder="Search purchase orders..."
			options={options}
			selectedOptions={selectedOptions}
			onChange={onSelectionChange}
			onSearchChange={onSearchChange}
			singleSelection={{ asPlainText: true }}
			isLoading={loading}
			isInvalid={isInvalid}
			isClearable
		/>
	)
}
